import { useEffect, useState } from 'react'
import { Button, Card, Chip, Icon, PageHead, StateBlock, useToast } from '@w7/ui'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { accountSecurityUrl, getAuthConfig, isOidcConfigured } from '@/auth/config'
import { isDemoSession } from '@/auth/oidc'
import { markMethodEnrolled, canonicalMfaMethodId, type MfaCredentialInfo } from '@/auth/mfaStatus'
import { webAuthnNeedsLocalhost } from '@/auth/webauthnHost'
import { useMfaStatus } from '@/api/hooks/shared/useMfaStatus'
import { keys } from '@/api/hooks/shared/keys'
import { MFA_METHODS, getMfaMethod } from './mfaMethods'

function formatWhen(ms?: number): string {
  if (!ms) return 'Recently'
  try {
    return new Date(ms).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return 'Recently'
  }
}

/** Credentials that belong on this method card only (never password). */
function credsForMethod(methodId: string, all: MfaCredentialInfo[]): MfaCredentialInfo[] {
  return all.filter((c) => {
    const t = c.type.toLowerCase()
    if (t === 'password' || t === 'password-history') return false
    if (methodId === 'totp') return t === 'otp' || t === 'totp' || t === 'hotp'
    if (methodId === 'recovery') return t.includes('recovery')
    // Details only on the canonical passkey card — not duplicated on fingerprint/key.
    if (methodId === 'passkey') return t.includes('webauthn')
    return false
  })
}

function isWebAuthnShortcut(methodId: string): boolean {
  return methodId === 'fingerprint' || methodId === 'security-key'
}

function defaultDeviceLabel(methodId: string): string {
  if (methodId === 'totp') return 'Authenticator'
  if (methodId === 'fingerprint') return 'Fingerprint / device'
  if (methodId === 'security-key') return 'Security key'
  if (methodId === 'recovery') return 'Recovery codes'
  return 'Passkey'
}

/** Hidden-by-default enrolled list — opens from a quiet “N enrolled” control. */
function EnrolledMenu({
  methodId,
  details,
}: {
  methodId: string
  details: MfaCredentialInfo[]
}) {
  const [open, setOpen] = useState(false)
  if (details.length === 0) return null
  const label =
    details.length === 1 ? '1 enrolled · details' : `${details.length} enrolled · details`

  return (
    <div style={{ marginTop: 12 }}>
      <Button
        variant="ghost"
        size="sm"
        icon={open ? 'close' : 'info'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </Button>
      {open ? (
        <div
          style={{
            marginTop: 8,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'color-mix(in srgb, var(--w7-fg, #fff) 6%, transparent)',
            border: '1px solid color-mix(in srgb, var(--w7-fg, #fff) 10%, transparent)',
          }}
        >
          <p className="w7-dim" style={{ margin: '0 0 8px', fontSize: 11.5 }}>
            Labels you set at enroll · no secrets shown
          </p>
          <ul
            style={{
              margin: 0,
              paddingLeft: '1.1rem',
              fontSize: 12.5,
              lineHeight: 1.55,
              color: 'var(--w7-fg-muted)',
            }}
          >
            {details.map((d) => (
              <li key={d.id}>
                <span style={{ color: 'var(--w7-fg)' }}>
                  {d.userLabel?.trim() || defaultDeviceLabel(methodId)}
                </span>
                <span className="w7-dim"> · added {formatWhen(d.createdDate)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Hub for optional 2FA. Active state + details stay on each method card only.
 */
export function SecuritySettingsPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const demo = isDemoSession()
  const oidc = isOidcConfigured()
  const url = accountSecurityUrl()
  const enrolled = search.get('enrolled')
  const qc = useQueryClient()
  const statusQuery = useMfaStatus()
  const status = statusQuery.data
  const passkeyHostBroken = webAuthnNeedsLocalhost(
    typeof window !== 'undefined' ? window.location.hostname : 'localhost',
    getAuthConfig().keycloakUrl || undefined,
  )

  useEffect(() => {
    if (!enrolled) return
    markMethodEnrolled(enrolled)
    void qc.invalidateQueries({ queryKey: keys.mfaStatus })
  }, [enrolled, qc])

  const activeIds = new Set([
    ...(status?.activeMethodIds ?? []),
    ...(enrolled ? [canonicalMfaMethodId(enrolled)] : []),
  ])
  const activeCreds = (status?.credentials ?? []).filter((c) => {
    const t = c.type.toLowerCase()
    return t !== 'password' && t !== 'password-history'
  })
  const enrolledMeta = enrolled ? getMfaMethod(enrolled) : undefined
  const enrolledCanonical = enrolled ? getMfaMethod(canonicalMfaMethodId(enrolled)) : undefined
  const anyMfa = activeIds.size > 0

  const openAdvanced = () => {
    if (demo) {
      toast.push({
        tone: 'info',
        message: 'Sign in with Keycloak to manage two-factor authentication',
        detail: 'Demo data has no IdP user.',
      })
      return
    }
    if (!url || !oidc) {
      toast.push({
        tone: 'warn',
        message: 'Sign-in is not configured',
        detail: 'Set VITE_KEYCLOAK_URL to enable Keycloak MFA management.',
      })
      return
    }
    location.assign(url)
  }

  return (
    <>
      <PageHead
        title="Two-factor authentication"
        subtitle="An extra step after your password — app code, fingerprint, passkey, or security key"
        actions={
          !demo ? (
            <Button
              variant="secondary"
              icon="refresh"
              busy={statusQuery.isFetching}
              onClick={() => void statusQuery.refetch()}
            >
              Refresh status
            </Button>
          ) : undefined
        }
      />

      {passkeyHostBroken ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock icon="warning" tone="error" title="Passkeys need localhost, not an IP address">
            Fingerprint / passkey registration fails with “invalid domain” when the console or
            Keycloak is opened as <code>127.0.0.1</code>. Use{' '}
            <code>http://localhost:5273</code> and set <code>VITE_KEYCLOAK_URL=http://localhost:8081</code>,
            then restart local Keycloak (<code>services/dev/keycloak.sh</code>). Authenticator app
            (TOTP) still works on either host.
          </StateBlock>
        </div>
      ) : null}

      {enrolled ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock
            icon="check"
            title={
              enrolledCanonical && enrolledMeta && enrolledCanonical.id !== enrolledMeta.id
                ? `${enrolledCanonical.title} is active`
                : `${enrolledMeta?.title ?? 'Method'} is active`
            }
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  search.delete('enrolled')
                  setSearch(search, { replace: true })
                }}
              >
                Dismiss
              </Button>
            }
          >
            {enrolledCanonical && enrolledMeta && enrolledCanonical.id !== enrolledMeta.id
              ? `You enrolled via ${enrolledMeta.title}. Keycloak stores that as one passkey — see the ${enrolledCanonical.title} card.`
              : `That method’s card below is highlighted. Next sign-in can ask for this second factor.`}
          </StateBlock>
        </div>
      ) : null}

      {!demo && !statusQuery.isLoading && !anyMfa && !enrolled ? (
        <p
          className="w7-dim"
          style={{
            margin: '0 0 var(--w7-s3)',
            fontSize: 12,
            lineHeight: 1.45,
            maxWidth: 40 * 16,
          }}
        >
          No second factor yet — an authenticator app or this device’s passkey are good starting points.
        </p>
      ) : null}

      <p className="w7-mfa-note">
        <span className="w7-mfa-note-label">Note · </span>
        Setup stays here (optional). After enroll, login can ask for it · fingerprint/security key
        share one passkey — only “This device” is Active.
      </p>

      {demo ? (
        <div style={{ marginBottom: 'var(--w7-s5)' }}>
          <StateBlock
            icon="shield"
            title="Demo session cannot enroll MFA"
            actions={
              <Button variant="primary" onClick={() => navigate('/login')}>
                Sign in with Keycloak
              </Button>
            }
          >
            Sign in with a Keycloak user to set up authenticator apps, passkeys, or fingerprint.
          </StateBlock>
        </div>
      ) : null}

      <div className="w7-grid w7-grid-2" style={{ gap: 'var(--w7-s4)' }}>
        {MFA_METHODS.map((m) => {
          const passkeyActive = activeIds.has('passkey')
          const coveredByPasskey = isWebAuthnShortcut(m.id) && passkeyActive
          const active = activeIds.has(m.id)
          const details = credsForMethod(m.id, activeCreds)
          return (
            <Card
              key={m.id}
              title={m.title}
              subtitle={m.body}
              style={
                active
                  ? {
                      border:
                        '1px solid color-mix(in srgb, var(--w7-profit, #3d9a6a) 70%, transparent)',
                      background:
                        'color-mix(in srgb, var(--w7-profit, #3d9a6a) 12%, var(--w7-surface, transparent))',
                      boxShadow: 'inset 3px 0 0 var(--w7-profit, #3d9a6a)',
                    }
                  : undefined
              }
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--w7-s3)',
                  flexWrap: 'wrap',
                  marginTop: 'var(--w7-s3)',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Icon name={m.icon} size={18} />
                  {active ? (
                    <Chip size="sm" tone="profit" icon="check">
                      Active
                    </Chip>
                  ) : coveredByPasskey ? (
                    <Chip size="sm" tone="info" icon="check">
                      Covered by passkey
                    </Chip>
                  ) : (
                    <Chip
                      size="sm"
                      tone={m.status === 'ready' ? 'info' : m.status === 'soon' ? 'warn' : 'muted'}
                    >
                      {m.statusLabel}
                    </Chip>
                  )}
                </span>
                {m.kcAction && m.actionLabel ? (
                  <Button
                    variant={active || coveredByPasskey ? 'secondary' : 'primary'}
                    size="sm"
                    icon="shield"
                    onClick={() => navigate(`/settings/security/${m.id}`)}
                    disabled={demo}
                  >
                    {active
                      ? 'Manage / re-enroll'
                      : coveredByPasskey
                        ? 'Add another device'
                        : m.actionLabel}
                  </Button>
                ) : null}
              </div>

              {coveredByPasskey && !active ? (
                <p className="w7-dim" style={{ margin: '12px 0 0', fontSize: 12.5 }}>
                  Keycloak stores one passkey credential — fingerprint and security keys are the same
                  store as “This device (passkey)”, not separate Active enrollments.
                </p>
              ) : null}

              {active && details.length > 0 ? (
                <EnrolledMenu methodId={m.id} details={details} />
              ) : active ? (
                <p className="w7-dim" style={{ margin: '12px 0 0', fontSize: 12.5 }}>
                  Enrolled for this account · next login can ask for this factor
                </p>
              ) : null}
            </Card>
          )
        })}
      </div>

      <p className="w7-dim" style={{ marginTop: 'var(--w7-s5)', fontSize: 13 }}>
        Need to remove a method?{' '}
        <button
          type="button"
          className="w7-linkish"
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            color: 'var(--w7-accent)',
            cursor: demo || !url ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
          }}
          disabled={demo || !url}
          onClick={openAdvanced}
        >
          Manage credentials in Keycloak
        </button>
      </p>
    </>
  )
}
